import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { RequireScope } from '@/components/common/RequireScope';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { LangDirection } from '@/components/common/LangDirection';
import { SupportChatMount } from '@/support/SupportChatMount';

// ── Lazy-loaded pages ──────────────────────────────────────────

const LoginPage = lazy(() => import('@/pages/LoginPage'));
const ChangePasswordPage = lazy(() => import('@/pages/ChangePasswordPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// Email identity (ADR-012) — public / pre-auth pages reached from emailed
// links (forgot-password / set-password / reset-password / verify-email).
// Registered as top-level siblings of /login + /change-password, NOT under
// ProtectedRoute><AppLayout> — same convention as ChangePasswordPage.
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage'));
const SetPasswordPage = lazy(() => import('@/pages/auth/SetPasswordPage'));
const HandoffPage = lazy(() => import('@/pages/auth/HandoffPage'));

// Retired staff workspace (ADR-033 D12): every /workspace route renders this one redirect to the
// Agent Console. The forked workspace code itself is deleted in a follow-up PR.
const AgentConsoleRedirect = lazy(() => import('@/pages/AgentConsoleRedirect'));

// Tenants
const TenantListPage = lazy(() => import('@/pages/tenants/TenantListPage'));
const TenantFormPage = lazy(() => import('@/pages/tenants/TenantFormPage'));
const TenantMindPage = lazy(() => import('@/pages/tenants/TenantMindPage'));
// CRM Integration settings (R12, ADR-006) is now reached via the Settings
// area's CRM tab (SettingsPage imports CrmSettingsPage directly); the
// standalone /crm-settings route redirects to /settings.

// Tenant Settings (WS3) — single container with Profile | Chat Appearance |
// General | Users | CRM tabs, scoped to the effective tenant.
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));

// My Settings (WS3 Feature 2) — per-user preferences, every authenticated role.
const MySettingsPage = lazy(() => import('@/pages/settings/MySettingsPage'));

// Users (RBAC admin platform — Wave 3b)
const UserListPage = lazy(() => import('@/pages/users/UserListPage'));
const UserFormPage = lazy(() => import('@/pages/users/UserFormPage'));

// Bots (legacy) — OP-559: BotListPage/BotFormPage are GONE, not just unrouted. They targeted
// /api/bots, which has no gateway route (OP-164), so every button on them 404'd. The real
// agent-to-channel binding lives under /bot-entities ("Bot Attachments").

// Agents
const AgentListPage = lazy(() => import('@/pages/agents/AgentListPage'));
const AgentFormPage = lazy(() => import('@/pages/agents/AgentFormPage'));
const KnowledgeSourceDetailPage = lazy(() => import('@/pages/agents/KnowledgeSourceDetailPage'));

// Knowledge Banks (Knowledge Ingestion v2)
const KnowledgeBanksListPage = lazy(() => import('@/pages/knowledge-banks/KnowledgeBanksListPage'));
const KnowledgeBankFormPage = lazy(() => import('@/pages/knowledge-banks/KnowledgeBankFormPage'));
const KnowledgeBankDetailPage = lazy(() => import('@/pages/knowledge-banks/KnowledgeBankDetailPage'));
const KnowledgeLibraryPage = lazy(() => import('@/pages/knowledge-library/KnowledgeLibraryPage'));

// Learning (top-level — backfill memory, run learning, manage Learning bank)
const LearningPage = lazy(() => import('@/pages/learning/LearningPage'));

// Bot Entities (Agent + Channel)
const BotEntityListPage = lazy(() => import('@/pages/bot-entities/BotEntityListPage'));
const BotEntityFormPage = lazy(() => import('@/pages/bot-entities/BotEntityFormPage'));

// Bots — tabbed container over Channels | Agents | Bot Attachments. Reached from
// the "Bot Management" nav group; the three underlying routes stay registered.
const BotsPage = lazy(() => import('@/pages/bots/BotsPage'));

// Bot's Mind — PLACEHOLDER page (title only); the editor body arrives with the
// follow-up Bot's Mind task. Nav entry + route land now (memory.manage).
const BotMindPage = lazy(() => import('@/pages/bot-mind/BotMindPage'));
const BotSkillsPage = lazy(() => import('@/pages/bot-skills/BotSkillsPage'));

// Channels
const ChannelListPage = lazy(() => import('@/pages/channels/ChannelListPage'));
const ChannelFormPage = lazy(() => import('@/pages/channels/ChannelFormPage'));
const ChannelEditPage = lazy(() => import('@/pages/channels/ChannelEditPage'));

// Configuration
const ConfigListPage = lazy(() => import('@/pages/configuration/ConfigListPage'));
const ConfigFormPage = lazy(() => import('@/pages/configuration/ConfigFormPage'));

// Message Templates (OP-184) — tenant-scoped; config.edit (admin + platformAdmin)
const TemplateListPage = lazy(() => import('@/pages/templates/TemplateListPage'));
const TemplateFormPage = lazy(() => import('@/pages/templates/TemplateFormPage'));

// Analytics v1 (2026-10-14) — restored; backed by /api/analytics/* (OP-163 closed).
const AnalyticsDashboardPage = lazy(() => import('@/pages/analytics/AnalyticsDashboardPage'));

// Audit
const AuditLogPage = lazy(() => import('@/pages/audit/AuditLogPage'));

// Ticket Imports (cold-start historical-ticket ingestion)
const TicketImportsPage = lazy(() => import('@/pages/ticket-imports/TicketImportsPage'));
const TicketImportMappingPage = lazy(() => import('@/pages/ticket-imports/TicketImportMappingPage'));

const App: React.FC = () => (
  <ErrorBoundary>
    <LangDirection />
    {/* BotableX's own support chat — signed-in users only, identified; see src/support/supportChat.ts */}
    <SupportChatMount />
    <BrowserRouter>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />

          {/* Forced password reset — PUBLIC / PRE-authentication. A temp-password
              account is rejected at /login with 403 password_reset_required (no
              token), so useAuth.login routes here with the typed credentials in
              router state; the page sets a new password and establishes the
              session. The page itself redirects to /login if reached without that
              state. NOT wrapped in ProtectedRoute — there is no session yet. */}
          <Route path="/change-password" element={<ChangePasswordPage />} />

          {/* Email identity (ADR-012) — PUBLIC / pre-auth. Reached from emailed
              links: forgot-password kicks off a reset; reset-password/set-password
              carry a one-time ?token=&tenant= (validated on mount, non-consuming);
              verify-email takes a typed numeric code (no token in the URL). None
              of these are wrapped in ProtectedRoute — there is no session yet. */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />

          {/* Sign-in hand-off from the marketing site — PUBLIC / pre-auth. Carries a single-use code
              in the URL fragment, redeems it, stores the session like a login. See HandoffPage. */}
          <Route path="/auth/handoff" element={<HandoffPage />} />

          {/* Authenticated layout */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <ProtectedRoute requiredPermission="dashboard.view">
                  <DashboardPage />
                </ProtectedRoute>
              }
            />

            {/* My Settings (WS3 Feature 2) — per-user preferences. Every
                authenticated role reaches this (agent/seniorAgent/admin/
                platformAdmin) — no permission gate and no tenant/platform
                <RequireScope>, mirroring the fact that the outer AppLayout
                route itself requires no specific permission. */}
            <Route
              path="my-settings"
              element={
                <ProtectedRoute>
                  <MySettingsPage />
                </ProtectedRoute>
              }
            />

            {/* Workspace — RETIRED (ADR-033 D12). /workspace and every /workspace/* deep link render
                the one AgentConsoleRedirect (the splat carries a conversation id across). The old
                workspace.view gate is kept, so a user without it still gets Forbidden, not a redirect.
                No RequireScope: the redirect needs no tenant. */}
            <Route
              path="workspace/*"
              element={
                <ProtectedRoute requiredPermission="workspace.view">
                  <AgentConsoleRedirect />
                </ProtectedRoute>
              }
            />

            {/* Tenants — cross-tenant management (platformAdmin only via
                the permission matrix). Migrated from allowedRoles to a
                fine-grained permission gate (Wave 3b RBAC). */}
            <Route
              path="tenants"
              element={
                <RequireScope scope="platform">
                  <ProtectedRoute requiredPermission="tenants.manage">
                    <TenantListPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            <Route
              path="tenants/new"
              element={
                <RequireScope scope="platform">
                  <ProtectedRoute requiredPermission="tenants.manage">
                    <TenantFormPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            <Route
              path="tenants/:tenantId/edit"
              element={
                <RequireScope scope="platform">
                  <ProtectedRoute requiredPermission="tenants.manage">
                    <TenantFormPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />

            {/* Users administration (Wave 3b) — manage portal users for the
                active tenant. Gated on users.manage (admin + platformAdmin). */}
            <Route
              path="users"
              element={
                <ProtectedRoute requiredPermission="users.manage">
                  <UserListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="users/new"
              element={
                <ProtectedRoute requiredPermission="users.manage">
                  <UserFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="users/:userId/edit"
              element={
                <ProtectedRoute requiredPermission="users.manage">
                  <UserFormPage />
                </ProtectedRoute>
              }
            />

            {/* Phase C — Tenant Mind editor (the active tenant's Mind). Available
                to any authenticated user with tenantMind.view. Edit ability is
                derived inside the page from tenantMind.edit (read-only otherwise).
                Cross-tenant access is enforced server-side; the Tenant Mind
                endpoint resolves the tenant from the route parameter, but the
                authenticated user's JWT claim must match. */}
            <Route
              path="tenant-mind"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="tenantMind.view">
                    <TenantMindPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />

            {/* Tenant Settings (WS3) — Profile | Chat Appearance | Users | CRM
                tabs, scoped to the effective tenant. */}
            <Route
              path="settings"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="config.edit">
                    <SettingsPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />

            {/* Conversation States (ADR-017) — now the "Conversation Statuses" section (`section=folders`) of
                Settings > Console Settings (ADR-033 D8; PR-A made General the default section, so the link names it). The standalone page is gone; the route stays so old links and
                bookmarks land on its new home. The destination is gated exactly as this route was
                (tenant scope + config.edit), so the redirect itself needs no guard. */}
            <Route
              path="conversation-states"
              element={<Navigate to="/settings?tab=consoleSettings&section=folders" replace />}
            />

            {/* CRM Integration settings (R12, ADR-006) — the standalone nav item
                was folded into Settings > CRM. The legacy /crm-settings deep
                link still resolves: redirect it to the Settings area so old
                bookmarks / links keep working. */}
            <Route path="crm-settings" element={<Navigate to="/settings" replace />} />

            {/* Agents */}
            <Route
              path="agents"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="bots.manage">
                    <AgentListPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            <Route
              path="agents/new"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="bots.manage">
                    <AgentFormPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            <Route
              path="agents/:id/edit"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="bots.manage">
                    <AgentFormPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            {/* Knowledge source detail — drill-in from the source row on
                the agent edit page. Shows the chunked text + enable/disable
                toggle. */}
            <Route
              path="agents/:id/knowledge/:sourceId"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="bots.manage">
                    <KnowledgeSourceDetailPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />

            {/* Knowledge Banks (v2) — list / create / edit / detail */}
            <Route
              path="knowledge-banks"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="knowledge.manage">
                    <KnowledgeBanksListPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            <Route
              path="knowledge-banks/new"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="knowledge.manage">
                    <KnowledgeBankFormPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            <Route
              path="knowledge-banks/:id"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="knowledge.manage">
                    <KnowledgeBankDetailPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            <Route
              path="knowledge-banks/:id/edit"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="knowledge.manage">
                    <KnowledgeBankFormPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            {/* ADR-036 Knowledge Library. Same gate as Knowledge Banks; the page itself renders nothing of the
                Library until GET /status says enabled (the server also 404s every Library route while off). */}
            <Route
              path="knowledge-library"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="knowledge.manage">
                    <KnowledgeLibraryPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />

            {/* Learning — multi-agent memory backfill + on-demand learning +
                system-managed Learning bank toggle. Sibling of Agents /
                Knowledge Banks; tenant resolves from the JWT. */}
            <Route
              path="learning"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="learning.review">
                    <LearningPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />

            {/* Bot Entities (Agent + Channel) */}
            <Route
              path="bot-entities"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="bots.manage">
                    <BotEntityListPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            <Route
              path="bot-entities/new"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="bots.manage">
                    <BotEntityFormPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            {/* OP-022 (2026-05-06) — edit route was missing; clicks from
                BotEntityListPage's row link "/bot-entities/{id}/edit" hit the
                global 404. The form already supports the :botId param via
                useParams, so this is a one-line route registration. */}
            <Route
              path="bot-entities/:botId/edit"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="bots.manage">
                    <BotEntityFormPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />

            {/* Bots — tabbed container (Channels | Agents | Bot Attachments).
                Reuses the same bots.manage gate as the three list routes it
                renders inline, so the tab surface can never be laxer than the
                pages inside it. NOTE: unrelated to the legacy /bots CRUD routes
                removed by OP-166 (bare /api/bots has no gateway route). */}
            <Route
              path="bots"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="bots.manage">
                    <BotsPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />

            {/* Bot's Mind — PLACEHOLDER body; gate is final. memory.manage =
                platformAdmin + admin only (seniorAgent/agent excluded). */}
            <Route
              path="bot-mind"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="memory.manage">
                    <BotMindPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />

            {/* ADR-041 bot Skills. Same gate as Knowledge Banks (knowledge.manage), which the server enforces on every
                route; not behind the bot-side switch, and the page says whether bots read skills yet. */}
            <Route
              path="bot-skills"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="knowledge.manage">
                    <BotSkillsPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />

            {/* Channels */}
            <Route
              path="channels"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="bots.manage">
                    <ChannelListPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            <Route
              path="channels/new"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="bots.manage">
                    <ChannelFormPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            {/* PART D (OP-166) — RESOLVED 2026-08-17. The edit route now exists, as a SEPARATE page
                rather than a mode on ChannelFormPage: that form is ~2,700 lines of create-time
                branching, and threading an edit mode through it is how a silent no-op gets built by
                accident. ChannelEditPage sends ONLY changed fields and re-seeds from the server
                response, so "saved" means the server said so.

                The original reasoning — "a silently-no-op edit is worse than no edit" — was right,
                and the absence still had a price: a WhatsApp channel created with the wrong
                phone_number_id rejected every inbound message with 401 and could not be corrected
                from the portal at all (the Azure Mongo is internal-only and `containerapp exec`
                returns 500 on that environment). Deliberately narrow — identity/routing fields
                only; see the page's own remarks for why credentials are excluded. */}
            <Route
              path="channels/:channelId/edit"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="bots.manage">
                    <ChannelEditPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />

            {/* ADR-021 A2 added a VIEW route, which is a different thing: an email channel has
                state that only exists AFTER creation (Microsoft connection status, the Connect
                button, Test Connection). Those lived solely in the just-created view, so losing the
                session during Microsoft's redirect — exactly what a sign-in does — left the channel
                unreachable and unconnectable. This route is read-only plus those actions. */}
            <Route
              path="channels/:channelId"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="bots.manage">
                    <ChannelFormPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />

            {/* Configuration — PART C (OP-166): raw config editor = platformAdmin-only ops tool; reuse the platformAdmin-exclusive tenants.manage gate without touching the locked permission catalog (OP-166) */}
            <Route
              path="configuration"
              element={
                <RequireScope scope="platform">
                  <ProtectedRoute requiredPermission="tenants.manage">
                    <ConfigListPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            <Route
              path="configuration/new"
              element={
                <RequireScope scope="platform">
                  <ProtectedRoute requiredPermission="tenants.manage">
                    <ConfigFormPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />

            {/* Message Templates (OP-184) — tenant-scoped governance surface.
                config.edit = admin + platformAdmin only (seniorAgent/agent
                excluded), matching the server gate on approve/reject. */}
            <Route
              path="templates"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="config.edit">
                    <TemplateListPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            <Route
              path="templates/new"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="config.edit">
                    <TemplateFormPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            <Route
              path="templates/:id/edit"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="config.edit">
                    <TemplateFormPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />

            {/* Analytics v1 (2026-10-14) — restored; backed by /api/analytics/* (OP-163 closed). */}
            <Route
              path="analytics"
              element={
                <ProtectedRoute requiredPermission="analytics.view">
                  <AnalyticsDashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Audit Log */}
            <Route
              path="audit"
              element={
                <ProtectedRoute requiredPermission="logs.view">
                  <AuditLogPage />
                </ProtectedRoute>
              }
            />

            {/* Ticket Imports (cold-start historical-ticket ingestion) */}
            <Route
              path="tenants/:tenantId/agent-configs/:agentId/ticket-imports"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="bots.manage">
                    <TicketImportsPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
            <Route
              path="tenants/:tenantId/ticket-import-mapping"
              element={
                <RequireScope scope="tenant">
                  <ProtectedRoute requiredPermission="bots.manage">
                    <TicketImportMappingPage />
                  </ProtectedRoute>
                </RequireScope>
              }
            />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  </ErrorBoundary>
);

export default App;
